import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../../contexts/AppContext';
import RolesContext from '../../../contexts/RolesContext';
import ToggleSmall from '../../Shared/Toggle/ToggleSmall';
import tenantApi from '../../../utils/api/tenant';
import { toast } from 'react-toastify';
import { useTenantFeature } from '../../../hooks/features';
import { Tooltip, Select, MenuItem, FormControl } from '@mui/material';
import { useLogo } from '../../../contexts/AppLogoContext';
import rolesApi from '../../../utils/api/roles';
import { FormContext, SchemaFieldsWithContextUI } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { ActionButtons } from '../../Shared/ActionButtons';
import { submitHandler } from '../../Shared/form';
import { useHistory } from 'react-router-dom';
import { Modal } from '@stormwater-software/lib-ui';
import { customDashboard } from '../../../utils/api/dashboard';
import DivisionContext from '../../../contexts/DivisionContext';
interface FeaturesFormat {
  feature: {
    /** `features.id`. Distinct from the `id` below, which is the `tenant_features` row. */
    id: number;
    feature_type: string;
    name: string;
    tooltip: string;
    /** SW2-2230. Set when this feature is a sub-toggle of another; null for a top-level one. */
    parent_feature_id?: number | null;
  };
  id: any;
  is_enabled: boolean;
  max_inspection_templates?: number;
}

/**
 * SW2-2230 — sub-features.
 *
 * A sub-feature refines its parent rather than standing alone, so its row is hidden entirely while
 * the parent is off. The relationship comes from `features.parent_feature_id`, not from a name map
 * kept here: hardcoding names meant every new sub-feature needed a frontend change, and the pairing
 * silently broke if a feature was renamed or seeded into a different section.
 *
 * Switching a parent off also switches its children off, but that is the **server's** job now —
 * `tenantService.updateFeatures` cascades in one transaction. Doing it here as follow-up requests
 * meant a child failure rolled back the parent's UI state after its own write had already
 * committed, leaving the screen showing ON while the database said OFF.
 */
const parentOf = (
  feature: FeaturesFormat,
  featuresList: FeaturesFormat[],
): FeaturesFormat | undefined =>
  feature.feature.parent_feature_id
    ? featuresList.find((f) => f.feature.id === feature.feature.parent_feature_id)
    : undefined;

const isSubFeature = (feature: FeaturesFormat): boolean =>
  !!feature.feature.parent_feature_id;

/**
 * Whether this row should be shown.
 *
 * A sub-feature whose parent is not in this list at all (different section, or filtered out) is
 * treated as top-level rather than hidden — hiding it would make it unreachable with no indication
 * why.
 */
const isParentEnabled = (
  feature: FeaturesFormat,
  featuresList: FeaturesFormat[],
): boolean => {
  if (!isSubFeature(feature)) {
    return true;
  }

  const parent = parentOf(feature, featuresList);

  return parent ? !!parent.is_enabled : true;
};

/**
 * Re-order so each sub-feature sits directly beneath its parent. The API returns features in
 * `features.id` order, which puts a sub-feature wherever it happened to be seeded — for the map
 * item selection, several rows below its parent, where the indent alone reads as arbitrary.
 * Parents keep their original relative order; orphans (parent not in this section) stay put.
 */
const orderWithSubFeatures = (featuresList: FeaturesFormat[]): FeaturesFormat[] => {
  const byParent = new Map<number, FeaturesFormat[]>();

  featuresList.forEach((f) => {
    const parent = parentOf(f, featuresList);

    if (parent) {
      byParent.set(parent.feature.id, [
        ...(byParent.get(parent.feature.id) ?? []),
        f,
      ]);
    }
  });

  if (!byParent.size) {
    return featuresList;
  }

  // Array.from rather than a spread: Map.values() is an iterator, and spreading one needs
  // --downlevelIteration under this tsconfig's target.
  const placed = new Set(
    Array.from(byParent.values())
      .reduce<FeaturesFormat[]>((all, children) => all.concat(children), [])
      .map((f) => f.id),
  );

  return featuresList.flatMap((f) =>
    placed.has(f.id) ? [] : [f, ...(byParent.get(f.feature.id) ?? [])],
  );
};

const Feature = (
  title: string,
  featuresList: FeaturesFormat[],
  handleToggle: (
    id: number,
    currentInclude: boolean,
    feature_type?: string,
  ) => void,
) => {
  const [collapse, setCollapse] = useState(false);
  const permCheck = useContext(RolesContext).userHasPermission;

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>{title}</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
        style={{ marginBottom: '20px' }}
      >
        {orderWithSubFeatures(featuresList).map((ele) => {
          // SW2-2230: a sub-feature is not shown at all until its parent is enabled.
          if (!isParentEnabled(ele, featuresList)) {
            return null;
          }

          const subFeatureIndent = isSubFeature(ele)
            ? { paddingLeft: '2rem' }
            : undefined;

          return ele.feature.tooltip ? (
            <div className='features-table-body' key={ele.id} style={subFeatureIndent}>
              <Tooltip
                title={ele.feature.tooltip}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <h3>{ele.feature.name}</h3>
              </Tooltip>
              <h3 className='features-table-body-toggle'>
                <ToggleSmall
                  isChecked={ele.is_enabled}
                  handleChange={() => handleToggle(ele.id, ele.is_enabled)}
                  disabled={!permCheck('all', 'Administration Features')}
                />
              </h3>
            </div>
          ) : (
            <div className='features-table-body' key={ele.id} style={subFeatureIndent}>
              <h3>{ele.feature.name}</h3>
              <h3 className='features-table-body-toggle'>
                <ToggleSmall
                  isChecked={ele.is_enabled}
                  handleChange={() =>
                    handleToggle(
                      ele.id,
                      ele.is_enabled,
                      ele.feature.feature_type,
                    )
                  }
                  disabled={!permCheck('all', 'Administration Features')}
                />
              </h3>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FeatureWithAction = (
  title: string,
  featuresList: FeaturesFormat[],
  handleAction: (id: number, feature_type?: string) => void,
) => {
  const [collapse, setCollapse] = useState(false);

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>{title}</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
        style={{ marginBottom: '20px' }}
      >
        {featuresList.map((ele) => {
          return ele.feature.tooltip ? (
            <div className='features-table-body' key={ele.id}>
              <Tooltip
                title={ele.feature.tooltip}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <h3>{ele.feature.name}</h3>
              </Tooltip>
              <div className='features-table-body-toggle'>
                <ActionButtons
                  primary='Run'
                  onClick={() => handleAction(ele.id, ele.feature.feature_type)}
                />
              </div>
            </div>
          ) : (
            <div className='features-table-body' key={ele.id}>
              <h3>{ele.feature.name}</h3>
              <div className='features-table-body-button'>
                <ActionButtons
                  primary='Run'
                  onClick={() => handleAction(ele.id, ele.feature.feature_type)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MultipleFormsFeature = (
  title: string,
  featuresList: FeaturesFormat[],
  handleToggle: (
    id: number,
    currentInclude: boolean,
    feature_type?: string,
  ) => void,
  tenantId: number,
) => {
  const [collapse, setCollapse] = useState(false);
  const permCheck = useContext(RolesContext).userHasPermission;

  // Find the multiple forms feature
  const multipleFormsFeature = featuresList.find(
    (ele) => ele.feature.name === 'Enable multiple forms per project'
  );

  const [maxFormsCount, setMaxFormsCount] = useState(1);

  // Update maxFormsCount when multipleFormsFeature data is loaded
  useEffect(() => {
    if (multipleFormsFeature?.max_inspection_templates) {
      setMaxFormsCount(multipleFormsFeature.max_inspection_templates);
    }
  }, [multipleFormsFeature?.max_inspection_templates]);

  const handleMaxFormsChange = async (event: any) => {
    const newValue = event.target.value;
    setMaxFormsCount(newValue);
    
    try {
      await tenantApi.updateMaxInspectionTemplates(tenantId, newValue);
      toast.success(`Maximum forms per project set to ${newValue}`);
    } catch (error) {
      toast.error('Error updating maximum forms per project');
      console.error('Error updating max inspection templates:', error);
    }
  };

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>{title}</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
        style={{ marginBottom: '20px' }}
      >
        {/* Regular features */}
        {orderWithSubFeatures(featuresList)
          .filter(
            (ele) => ele.feature.name !== 'Enable multiple forms per project',
          )
          .map((ele) => {
            // SW2-2230: a sub-feature is not shown at all until its parent is enabled.
            if (!isParentEnabled(ele, featuresList)) {
              return null;
            }

            const subFeatureIndent = isSubFeature(ele)
              ? { paddingLeft: '2rem' }
              : undefined;

            return ele.feature.tooltip ? (
              <div
                className='features-table-body'
                key={ele.id}
                style={subFeatureIndent}
              >
                <Tooltip
                  title={ele.feature.tooltip}
                  placement='top'
                  arrow
                  enterTouchDelay={0}
                >
                  <h3>{ele.feature.name}</h3>
                </Tooltip>
                <h3 className='features-table-body-toggle'>
                  <ToggleSmall
                    isChecked={ele.is_enabled}
                    handleChange={() => handleToggle(ele.id, ele.is_enabled)}
                    disabled={!permCheck('all', 'Administration Features')}
                  />
                </h3>
              </div>
            ) : (
              <div
                className='features-table-body'
                key={ele.id}
                style={subFeatureIndent}
              >
                <h3>{ele.feature.name}</h3>
                <h3 className='features-table-body-toggle'>
                  <ToggleSmall
                    isChecked={ele.is_enabled}
                    handleChange={() =>
                      handleToggle(
                        ele.id,
                        ele.is_enabled,
                        ele.feature.feature_type,
                      )
                    }
                    disabled={!permCheck('all', 'Administration Features')}
                  />
                </h3>
              </div>
            );
          })}
        
        {/* Custom Multiple Forms Feature */}
        {multipleFormsFeature && (
          <div className='features-table-body' key={multipleFormsFeature.id}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <h3 style={{ margin: 0, flex: 1 }}>Enable multiple forms per project</h3>
              <FormControl size="small" style={{ minWidth: 80 }}>
                <Select
                  value={maxFormsCount}
                  onChange={handleMaxFormsChange}
                  disabled={!multipleFormsFeature.is_enabled || !permCheck('all', 'Administration Features')}
                  style={{ 
                    height: '32px',
                    fontSize: '14px'
                  }}
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div style={{ marginLeft: 'auto', paddingLeft: '20px' }}>
                <ToggleSmall
                  isChecked={multipleFormsFeature.is_enabled}
                  handleChange={() =>
                    handleToggle(
                      multipleFormsFeature.id,
                      multipleFormsFeature.is_enabled,
                      multipleFormsFeature.feature.feature_type,
                    )
                  }
                  disabled={!permCheck('all', 'Administration Features')}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Roles = () => {
  const formContext = useContext(FormContext);
  const [rolesWithStatus, setRolesWithStatus] = useState<any[]>([]);
  const [collapse, setCollapse] = useState(false);
  const history = useHistory();
  

  useEffect(() => {
    let isMounted = true;

    rolesApi.getRolesWithStatus().then((res) => {
      if (isMounted) {
        setRolesWithStatus(res);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (formData: any) => {
    const updatedRoles = rolesWithStatus.map((ele) => {
      const isEnabled = formData.roles.some((role: any) => role.id === ele.id);
      
      return {
        id: ele.tenantRoles[0].id,
        role_id: ele.id,
        name: ele.name,
        is_enabled: isEnabled,
      };
    });

    rolesApi.updateTenantRoles({ rolesWithStatus: updatedRoles }).then(() => {
      toast.success('Roles updated successfully');
      history.go(0);
    });
  };

  if (rolesWithStatus.length === 0) {
    return (
      <div
        className='settings-tenant-main-container'
        style={{ marginBottom: '5rem' }}
      >
        <div
          className='settings-main-tenant-text'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '30px',
          }}
        >
          <h3>Roles</h3>
        </div>
        <hr></hr>
        Loading...
      </div>
    );
  }

  return (
    <div
      className='settings-tenant-main-container'
      style={{ marginBottom: '5rem' }}
    >
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>Roles</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
      >
        <SchemaFieldsWithContextUI
          schema={{
            roles: {
              label: 'Roles',
              controlType: UIControlType.customTagSelect,
              isMulti: true,
              openOnFocus: true,
              showSelectAll: true,
              labelKey: 'name',
              valueKey: 'id',
              options: rolesWithStatus,
              showClearAll: true,
              style: {
                width: '100%',
                height: 'auto',
                marginTop: '10px',
                marginRight: '0.5rem',
                marginLeft: '0.5rem',
              },
            },
          }}
          initialFormData={{
            roles: rolesWithStatus.filter(
              (ele) => ele.tenantRoles[0].is_enabled,
            ),
          }}
        />
        <ActionButtons
          primary='Save'
          onClick={(e: any) => submitHandler(formContext, e, handleSubmit)}
        />
      </div>
    </div>
  );
};

export const AllFeatures = (props: any) => {
  const [featuresList, setfeaturesList] = useState<FeaturesFormat[]>([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<FeaturesFormat | null>(null);
  const appContext = useContext(AppContext);
  const divisionContext = useContext(DivisionContext);
  const tenantFeatureQuery = useTenantFeature();
  const { refetchLogo } = useLogo();
  const user = appContext.get("user");

  const userTenantId =
    appContext &&
    appContext.state &&
    appContext.state.user &&
    appContext.state.user.tenantId;

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = () => {
      tenantApi.features.index(userTenantId).then((res) => {
        if (isMounted) {
          setfeaturesList(res);
        }
      });
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggle = async (
    id: number,
    currentInclude: boolean,
    feature_type: string | null = null,
  ) => {
    // Spread rather than rebuild: the old literal listed three `feature` keys by hand and so
    // dropped `id` and `parent_feature_id`, which are what the sub-feature nesting reads. Toggling
    // anything flattened the tree until the next refetch.
    const updatedFeaturesList = featuresList.map((ele) =>
      ele.id === id ? { ...ele, is_enabled: !currentInclude } : ele,
    );

    /**
     * SW2-2230 — no client-side cascade.
     *
     * Switching a parent off still switches its sub-features off, but `tenantService.updateFeatures`
     * now does it in the same transaction as the parent write. This used to fire follow-up requests
     * from `.then`, inside the same promise chain: a child failure therefore landed in `.catch`,
     * which rolled the *parent* row back in the UI even though its own write had already committed.
     * The screen said ON, the database said OFF, and the child stayed enabled but hidden.
     *
     * Refetching is what reflects the cascade, so the local update below is only ever about the one
     * row the user clicked — which is also exactly what `.catch` is entitled to roll back.
     */
    setfeaturesList(updatedFeaturesList);
    tenantApi.features
      .update(userTenantId, id, !currentInclude)
      .then(() => {
        tenantFeatureQuery.refetch();
        if (feature_type === 'white_label') {
          refetchLogo(userTenantId);
          props.setIsWhiteLabel(!currentInclude);
        }
        toast.success(
          `Feature ${
            currentInclude === false ? 'Enabled' : 'Disabled'
          } Successfully!`,
        );
      })
      .catch((error: any) => {
        // The server rejects enabling a sub-feature whose parent is off. Surface its message rather
        // than the generic one — "Enable the parent feature before enabling this one" is actionable
        // and the fallback is not.
        toast.error(
          error?.response?.data?.message || 'Error updating feature',
        );

        // Rolls back the one row that failed. Safe to do unconditionally now that this handler no
        // longer owns a cascade: the only write it made was this row's.
        setfeaturesList(
          featuresList.map((ele) =>
            ele.id === id ? { ...ele, is_enabled: currentInclude } : ele,
          ),
        );
      });
  };

  const handleAction = async (id: number, feature_type?: string) => {
    try {
      const feature = featuresList.find((f) => f.id === id);

      if (feature && feature_type === 'national_dashboard_script') {
        setSelectedFeature(feature);
        setShowActionModal(true);
      }
    } catch (error) {
      toast.error('Error opening feature details');
    }
  };

  const executeFeatureAction = async () => {
    try {
      if (selectedFeature) {
        if (selectedFeature.feature.feature_type === 'national_dashboard_script') {
          const divisionId = (divisionContext && divisionContext.selectedDivision) ? divisionContext.selectedDivision.id : 1;

          await customDashboard.fetch(`/divisions/${divisionId}/dashboardTabs/runDenormalizedFindingScript`);
          
          toast.success('National Dashboard Script started successfully!');
        } else {
          toast.success('Action executed successfully!');
        }
        setShowActionModal(false);
        setSelectedFeature(null);
      }
    } catch (error) {
      console.error('Error executing feature action:', error);
      toast.error('Error executing action. Please try again.');
    }
  };

  const ModalForFeatureWithAction = () => {
    return(
      <Modal
        show={showActionModal}
        title="Are you sure you want to run this script?"
        handleClose={() => {
          setShowActionModal(false);
          setSelectedFeature(null);
        }}
        handleSubmit={executeFeatureAction}
        submitBtnText='Run'
        cancelBtn={true}
        cancelBtnText='Close'
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.5', 
            color: '#333',
            margin: '0'
          }}>
            This may take up to 5-10 minutes to complete and will refresh dashboard data for all tenants. It is recommended to run this during non-peak hours of operation.
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <div>
      {Feature(
        'White Label',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'white_label',
        ),
        handleToggle,
      )}
      {Feature(
        'Precipitation Tracking',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'precipitation_tracking',
        ),
        handleToggle,
      )}
      {Feature(
        'Permission Group',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'permission_group',
        ),
        handleToggle,
      )}
      {Feature(
        'Site Map Editor',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'site_map_editor',
        ),
        handleToggle,
      )}
      {Feature(
        'Reporting',
        featuresList.filter((ele) => ele.feature.feature_type === 'reporting'),
        handleToggle,
      )}
      {MultipleFormsFeature(
        'Project / Findings',
        featuresList.filter(
          (ele) =>
            ele.feature.feature_type === 'projects_findings' &&
            ele.feature.name !== 'Authorization & Agreement Gate',
        ),
        handleToggle,
        userTenantId,
      )}
      {Feature(
        'Inspections',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'authorization_agreement',
        ),
        handleToggle,
      )}
      {Feature(
        'Public View',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'public_view_qr_code',
        ),
        handleToggle,
      )}
      {Feature(
        'System Settings',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'system_settings',
        ),
        handleToggle,
      )}
      {(user.email === 'mmurray@sw2.net' || user.email === 'sqa_superadmin@gmail.com') &&
       FeatureWithAction(
        'National Dashboard Script',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'national_dashboard_script',
        ),
        handleAction,
      )}
      {Roles()}

      {ModalForFeatureWithAction()}
    </div>
  );
};
